import React, { Suspense, lazy } from 'react';
import { MotionConfig } from 'framer-motion';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
  useNavigate,
} from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider } from '@carton-org/react-neumorphism';
import { queryClient } from './lib/queryClient';
import { isSuperAdminClient, isDevHost } from './lib/superAdmin';
import { analyticsDestination } from './lib/analyticsRedirect';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BackgroundEffectsProvider } from './contexts/BackgroundEffectsContext';
import { FrostedGlassProvider } from './contexts/FrostedGlassContext';
import { DarkModeProvider } from './contexts/DarkModeContext';
import { ConfirmProvider } from './components/ui/useConfirm';
import Layout from './components/Layout';
import PageSkeleton from './components/PageSkeleton';
import {
  LandingHeroSkeleton,
  LandingLoader,
  LandingOuterFallback,
} from './landing/LandingSkeleton';
import PageTransition from './components/PageTransition';
import LockedPage from './components/LockedPage';
import {
  hasCompletedOnboarding as hasCompletedOnboardingUtil,
  leadsTeam,
} from './utils/onboarding';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import Login from './pages/Login';
import Register from './pages/Register';
import Onboarding from './pages/Onboarding';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
// Eager-loaded — must render on every authenticated page so a code-split
// delay can't leave the banner / block blank during first paint.
import TrialBanner from './components/trial/TrialBanner';
import OwnerSubscriptionRequiredBlock from './components/trial/OwnerSubscriptionRequiredBlock';
import 'react-toastify/dist/ReactToastify.css';
import ToastContainerWrapper from './components/ToastContainerWrapper';
import CookieConsent from './components/CookieConsent';
import './App.css';
import { useEffect, useRef } from 'react';
import { prefetchCommon, prefetchData } from './utils/prefetch';
import GoogleSignInScope from './components/auth/GoogleSignInScope';

import {
  lanuraColorsConfig,
  lanuraTypographyConfig,
  lanuraBorderConfig,
  lanuraNeumorphismConfig,
} from './theme/neumorphismTheme';

/* No webpackPrefetch hints on any route. A hint is not a call: webpack adds
 * <link rel=prefetch> for the chunk the moment main.js loads, for everyone,
 * so a logged-OUT landing visitor downloaded the whole portal (measured 29
 * Sep: 803 KB gzip of 1,248 KB, livekit and recharts included). Logged-in
 * users still get their pages warmed: prefetchCommon() after login, and
 * prefetchPage() on sidebar hover (utils/prefetch.ts). */
const Dashboard = lazy(() => import('./pages/Dashboard'));
const TryLanura = lazy(() => import('./pages/TryLanura'));
const ApiSession = lazy(() => import('./pages/ApiSession'));
const Developers = lazy(() => import('./pages/Developers'));
const DevelopersDocs = lazy(() => import('./pages/DevelopersDocs'));
const SimulationSession = lazy(() => import('./pages/SimulationSession'));
const Referral = lazy(() => import('./pages/Referral'));
const RefLanding = lazy(() => import('./pages/RefLanding'));
const BaselineQuiz = lazy(() => import('./pages/BaselineQuiz'));
const BaselineQuizContext = lazy(() => import('./pages/BaselineQuizContext'));
const BaselineQuizSimulation = lazy(() => import('./pages/BaselineQuizSimulation'));
const BaselineQuizProfile = lazy(() => import('./pages/BaselineQuizProfile'));
const BaselineQuizSnapshot = lazy(() => import('./pages/BaselineQuizSnapshot'));
const BaselineQuizFocus = lazy(() => import('./pages/BaselineQuizFocus'));
const BaselineSnapshot = lazy(() => import('./pages/BaselineSnapshot'));
// Self-serve owner sign-up wizard (public-rendered; handles its own auth/resume).
const GetStarted = lazy(() => import('./pages/GetStarted'));
const GetStartedCancelled = lazy(() => import('./pages/GetStartedCancelled'));
const GetStartedSuccess = lazy(() => import('./pages/GetStartedSuccess'));
// Public marketing pages (newUI port) — lazy so the Tailwind/AOS/sonner
// landing bundle stays off the portal's critical path for logged-in users.
const LandingLayout = lazy(() => import('./landing/LandingLayout'));
const LandingHome = lazy(() =>
  import('./landing/pages/HomePage').then((m) => ({ default: m.HomePage })),
);
const LandingUseCases = lazy(() =>
  import('./landing/pages/UseCasesPage').then((m) => ({ default: m.UseCasesPage })),
);
const LandingPrivacy = lazy(() =>
  import('./landing/pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })),
);
const LandingTerms = lazy(() =>
  import('./landing/pages/TermsPage').then((m) => ({ default: m.TermsPage })),
);
const LandingContact = lazy(() =>
  import('./landing/pages/ContactPage').then((m) => ({ default: m.ContactPage })),
);
const LandingBlogIndex = lazy(() =>
  import('./landing/pages/BlogIndexPage').then((m) => ({ default: m.BlogIndexPage })),
);
const LandingBlogArticle = lazy(() =>
  import('./landing/pages/BlogArticlePage').then((m) => ({ default: m.BlogArticlePage })),
);
// Previous landing design's content, rebuilt in the new theme and rendered
// under LandingLayout (new Header/Footer). Reached via the footer "Classic Home".
const LandingClassic = lazy(() =>
  import('./landing/pages/ClassicHomePage').then((m) => ({ default: m.ClassicHomePage })),
);
const ConnectCalendar = lazy(() => import('./pages/ConnectCalendar'));
const Settings = lazy(() => import('./pages/Settings'));
const SettingsPreferences = lazy(() => import('./pages/SettingsPreferences'));
const PracticeConversationEnhanced = lazy(() => import('./pages/PracticeConversationEnhanced'));
const PracticeReview = lazy(() => import('./pages/PracticeReview'));
const SimulationReview = lazy(() => import('./pages/SimulationReview'));
const CreateScenario = lazy(() => import('./pages/CreateScenario'));
const PracticePresentation = lazy(() => import('./pages/PracticePresentation'));
const PresentationReview = lazy(() => import('./pages/PresentationReview'));
const Reflection = lazy(() => import('./pages/Reflection'));
const Practice = lazy(() => import('./pages/Practice'));
const Progress = lazy(() => import('./pages/Progress'));
const Pathways = lazy(() => import('./pages/Pathways'));
const PathwaySituation = lazy(() => import('./pages/PathwaySituation'));
const ManagerOverview = lazy(() => import('./pages/ManagerOverview'));

/* Home is the team view for anyone who manages one (MH1, MQ1), and the
   learner home for everyone else. A manager is not going to practise first. */
const HomePage: React.FC = () => {
  const { can } = useAuth() as any;
  return typeof can === 'function' && can('manageTeam') ? <ManagerOverview /> : <Dashboard />;
};
// The manager journey's four pages, one job each (option 3a). Overview
// prioritises, Team says who, Practice Plans drives action, Analytics
// explains why. /admin/analytics still answers and redirects into them.
const TeamMembers = lazy(() => import('./pages/TeamMembers'));
const TeamAnalytics = lazy(() => import('./pages/TeamAnalytics'));
const PracticePlans = lazy(() => import('./pages/PracticePlans'));
// superAdmin only, and never prefetched — one identity uses it.
const InternalConsole = lazy(() => import('./pages/InternalConsole'));
const RubricEditor = lazy(() => import('./pages/RubricEditor'));
// Where the developer verification email lands. Must sit BEFORE /developers/*
// or the dashboard's catch-all swallows it.
const DevelopersVerifyEmail = lazy(() => import('./pages/DevelopersVerifyEmail'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const EmailLinkLanding = lazy(() => import('./pages/EmailLinkLanding'));
const Subscription = lazy(() => import('./pages/Subscription'));
// CheckoutReturn is retained as a backwards-compat alias for any
// CLIENT_INTERMEDIATE_SESSION_PAGE_URI value still set in .env to the
// old path. New checkout sessions point at /subscription/success.
const CheckoutReturn = lazy(() => import('./pages/CheckoutReturn'));
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'));
const CheckoutCancelled = lazy(() => import('./pages/CheckoutCancelled'));
const Changelog = lazy(() => import('./pages/Changelog'));
const JoinTeam = lazy(() => import('./pages/JoinTeam'));

function PathwaysRoleRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? `/pathways?role=${encodeURIComponent(id)}` : '/pathways'} replace />;
}

/** Redirect that keeps the query string (e.g. the ?sessionId= a retired
 *  onboarding URL was carrying) so the replacement page can still use it. */
function LegacyRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
}

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth() as any;
  const location = useLocation();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  /* `replace`, and remember where they were headed. Without replace this pushed
     a history entry, so Back landed on the page that had just bounced them. The
     `from` state lets /login return them to the page they actually asked for
     instead of dropping everyone on the dashboard — which is what made a
     transient auth blip look like "your session died". */
  if (!user) {
    return (
      <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
    );
  }

  // Non-owner teammates whose workspace owner's trial has expired AND owner
  // isn't paying — full-screen block, no in-app navigation. Set on /auth/me
  // by the gating middleware in backend/middleware/auth.js. Owners with
  // expired trials are not blocked here — they keep nav to Dashboard /
  // Settings / Subscription; their gating happens at the API layer via 402.
  if (user.role !== 'owner' && user.ownerSubscriptionExpired) {
    return <OwnerSubscriptionRequiredBlock />;
  }

  return <>{children}</>;
};

/** Redirects to `fallback` (default: /dashboard) if the user lacks the given permission. */
const PermissionRoute: React.FC<{
  permission: keyof import('./types').UserPermissions;
  fallback?: string;
  children: React.ReactNode;
}> = ({ permission, fallback = '/dashboard', children }) => {
  const { can, loading } = useAuth();
  if (loading) return <PageSkeleton />;
  return can(permission) ? <>{children}</> : <Navigate to={fallback} replace />;
};

/**
 * The old /admin/analytics, pointed at whichever page now does that job.
 *
 * The page became three (option 3a), and plenty of links still carry
 * `?tab=employees` or `?tab=pilot`. Rather than break them or leave the old
 * page as a second way to see the same thing, the URL keeps working and
 * forwards, query and all. The mapping is in lib/analyticsRedirect.ts.
 */
const AnalyticsRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={analyticsDestination(location.search)} replace />;
};

/**
 * Gates a route behind onboarding completion. Pages whose content is empty or
 * meaningless before the baseline diagnostic (Pathways, Progress) render a
 * locked unlock-screen instead of the page — pairs with the sidebar lock in
 * Layout.tsx. `locked` lets each route supply page-specific copy.
 */
const OnboardingRoute: React.FC<{
  children: React.ReactNode;
  locked?: React.ComponentProps<typeof LockedPage>;
}> = ({ children, locked }) => {
  const { user, loading } = useAuth() as any;
  if (loading) return <PageSkeleton />;
  return hasCompletedOnboardingUtil(user) ? <>{children}</> : <LockedPage {...locked} />;
};

const AppRoutes: React.FC = () => {
  const { user, refreshUser } = useAuth() as any;
  const location = useLocation();
  const navigate = useNavigate();
  const hasCompletedOnboarding = hasCompletedOnboardingUtil(user);
  // MQ1, Jenny's manager QA: someone who runs a team lands on their Home, not
  // on the learner diagnostic. They can still take it from Settings.
  const landsOnHome = hasCompletedOnboarding || leadsTeam(user);
  // Only send to onboarding diagnostic when user just registered (landed on /register with no onboarding signal).
  // Existing users going to / or /login always go to dashboard; they can start diagnostic via CTA if needed.
  const withSearch = (to: string) =>
    location.search ? `${to}${to.includes('?') ? '&' : '?'}${location.search.slice(1)}` : to;

  // Global axios 402-trial-expired listener (api.ts dispatches the event).
  // Owner whose trial ran out → redirect to /subscription with the attempted
  // path so we can deep-link back after they convert.
  useEffect(() => {
    const handler = (e: Event) => {
      // Dev/localhost: the superAdmin must ALWAYS keep access to /settings
      // and the Internal Console at /console. The admin panels (PlanAdmin,
      // Stripe mode toggle) are exactly what they need when billing state is
      // absent or broken, so a stray 402 from a gated API must not bounce them
      // off the page. Server-side authorization is unaffected (this only
      // skips the client redirect).
      if (
        isDevHost() &&
        isSuperAdminClient(user) &&
        (window.location.pathname.startsWith('/settings') ||
          window.location.pathname.startsWith('/console'))
      )
        return;
      const detail = (e as CustomEvent).detail || {};
      const upgradeUrl = detail.upgradeUrl || '/subscription';
      const attempted = detail.attemptedPath
        ? `?from=${encodeURIComponent(detail.attemptedPath)}`
        : '';
      navigate(`${upgradeUrl}${attempted}`, { replace: true });
    };
    window.addEventListener('auth:trial-expired' as any, handler as any);
    return () => window.removeEventListener('auth:trial-expired' as any, handler as any);
  }, [navigate, user]);

  // Mid-session: teammate hits a 402 with reason 'owner_subscription_required'
  // → refresh /auth/me so the user object picks up the freshly-true
  // ownerSubscriptionExpired flag → ProtectedRoute re-renders with the
  // block screen. The flag persists across reloads (it's read from
  // /auth/me on bootstrap), so this listener is only the "didn't have to
  // wait for the next refresh" path.
  useEffect(() => {
    if (!refreshUser) return;
    const handler = () => {
      try {
        refreshUser();
      } catch (_) {}
    };
    window.addEventListener('auth:owner-subscription-required' as any, handler as any);
    return () =>
      window.removeEventListener('auth:owner-subscription-required' as any, handler as any);
  }, [refreshUser]);

  // Data prefetch — once per session, after auth resolves. Warms the
  // cachedGet entries behind every sidebar destination + the Settings
  // tabs (role-gated) at browser idle, so first navigation hits warm
  // cache instead of a cold fetch. See utils/prefetch.ts for the wave
  // structure + TTL trade-offs.
  const dataPrefetchedRef = useRef(false);
  useEffect(() => {
    if (!user || dataPrefetchedRef.current) return;
    dataPrefetchedRef.current = true;
    try {
      prefetchData(user);
    } catch (_) {
      /* best-effort */
    }
    // Route-bundle prefetch (Dashboard/Practice/Pathways/Progress chunks) is
    // ALSO gated on auth: for a logged-out visitor on the marketing pages it
    // downloaded and EVALUATED the whole portal (recharts included) during
    // scroll pauses, producing multi-second long tasks that read as scroll lag.
    try {
      prefetchCommon();
    } catch (_) {
      /* best-effort */
    }
  }, [user]);

  return (
    <Routes>
      {/* Public marketing pages (newUI port) under a shared LandingLayout
          (Header + Footer + background blobs + AOS), rendered via <Outlet>.
          Each page has its own Suspense boundary so navigating between landing
          pages keeps the chrome mounted (only the page area shows the
          fallback). `/` still redirects logged-in users to the app. */}
      <Route
        element={
          <Suspense fallback={<LandingOuterFallback />}>
            <LandingLayout />
          </Suspense>
        }
      >
        <Route
          path="/"
          element={
            user ? (
              <Navigate to={landsOnHome ? '/dashboard' : '/baseline-quiz'} replace />
            ) : (
              <Suspense fallback={<LandingHeroSkeleton />}>
                <LandingHome />
              </Suspense>
            )
          }
        />
        <Route
          path="/use-cases"
          element={
            <Suspense fallback={<LandingLoader />}>
              <LandingUseCases />
            </Suspense>
          }
        />
        <Route
          path="/privacy"
          element={
            <Suspense fallback={<LandingLoader />}>
              <LandingPrivacy />
            </Suspense>
          }
        />
        <Route
          path="/terms"
          element={
            <Suspense fallback={<LandingLoader />}>
              <LandingTerms />
            </Suspense>
          }
        />
        <Route
          path="/contact"
          element={
            <Suspense fallback={<LandingLoader />}>
              <LandingContact />
            </Suspense>
          }
        />
        <Route
          path="/blog"
          element={
            <Suspense fallback={<LandingLoader />}>
              <LandingBlogIndex />
            </Suspense>
          }
        />
        <Route
          path="/blog/:slug"
          element={
            <Suspense fallback={<LandingLoader />}>
              <LandingBlogArticle />
            </Suspense>
          }
        />
        {/* Classic-home content under the new Header/Footer; linked from the footer. */}
        {/* <Route path="/home-classic" element={<Suspense fallback={<LandingLoader />}><LandingClassic /></Suspense>} /> */}
      </Route>
      {/* An authenticated user landing on /login almost always got here from a
          bounce, not by choice. Send them back to the page they asked for when
          we know it and onboarding is done; otherwise the usual destinations. */}
      <Route
        path="/login"
        element={
          user ? (
            <Navigate
              to={
                landsOnHome
                  ? typeof (location.state as any)?.from === 'string'
                    ? (location.state as any).from
                    : '/dashboard'
                  : '/baseline-quiz'
              }
              replace
            />
          ) : (
            <GoogleSignInScope>
              <Login />
            </GoogleSignInScope>
          )
        }
      />
      <Route
        path="/register"
        element={
          user ? (
            <Navigate to={landsOnHome ? '/dashboard' : '/baseline-quiz'} replace />
          ) : new URLSearchParams(window.location.search).has('companyId') ||
            new URLSearchParams(window.location.search).has('lanuraInvite') ||
            /* A referral link. Without this, every link a referrer shared
               landed on the login screen, because this route is otherwise
               invite-only and ?ref= matched neither of the invite params. */
            new URLSearchParams(window.location.search).has('ref') ? (
            <GoogleSignInScope>
              <Register />
            </GoogleSignInScope>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/forgot-password"
        element={
          user ? (
            <Navigate to={landsOnHome ? '/dashboard' : '/baseline-quiz'} replace />
          ) : (
            <ForgotPassword />
          )
        }
      />
      <Route
        path="/reset-password"
        element={
          user ? (
            <Navigate to={landsOnHome ? '/dashboard' : '/baseline-quiz'} replace />
          ) : (
            <ResetPassword />
          )
        }
      />
      {/* Personal links in emails. Public on purpose: it decides whether the
          signed-in account is the one the email was for. */}
      <Route path="/email-link" element={<EmailLinkLanding />} />
      <Route
        path="/verify-email"
        element={
          user ? (
            <Navigate to={landsOnHome ? '/dashboard' : '/baseline-quiz'} replace />
          ) : (
            <VerifyEmail />
          )
        }
      />
      {/* Team invite link. Public on purpose: the page itself decides whether
          this is a signed-in person joining or a stranger who needs an account
          first, and sends the stranger to /register carrying both ids. */}
      <Route path="/join" element={<JoinTeam />} />
      {/* Public release notes — no auth; works for signed-in and anonymous visitors alike. */}
      <Route path="/changelog" element={<Changelog />} />
      {/* Alias — same page; /changelog stays canonical. */}
      <Route path="/release-notes" element={<Changelog />} />
      <Route path="/onboarding" element={<Onboarding />} />
      {/* Self-serve owner sign-up — public-rendered; the wizard manages auth +
          resume internally, so it must NOT be wrapped in a user-redirect. */}
      <Route
        path="/get-started"
        element={
          <GoogleSignInScope>
            <GetStarted />
          </GoogleSignInScope>
        }
      />
      {/* Signup checkout cancel — PUBLIC + standalone (no app shell) so a
          cancelled self-serve signup never sees the logged-in interface. */}
      <Route path="/get-started/cancelled" element={<GetStartedCancelled />} />
      {/* Signup checkout success — PUBLIC + standalone. Provisions + auto-logs-in
          the new paid owner, then runs the invite step. */}
      <Route path="/get-started/success" element={<GetStartedSuccess />} />
      {/* PUBLIC showcase: one free simulation + report, no account needed */}
      <Route path="/try" element={<TryLanura />} />
      {/* PUBLIC hosted session page (Developer API) — token-authed via ?t=, no login */}
      <Route path="/api-session/:id" element={<ApiSession />} />
      {/* PUBLIC developer dashboard (developers.lanura.ai) — own dashboard-JWT auth */}
      {/* PUBLIC API reference — no account needed. Static path outranks the splat below. */}
      <Route path="/developers/docs" element={<DevelopersDocs />} />
      <Route path="/developers/verify-email" element={<DevelopersVerifyEmail />} />
      <Route path="/developers/*" element={<Developers />} />
      {/* PUBLIC referral portal — its own JWT, its own login page. A referrer
          is a standalone entity, so this never touches the app session and an
          address may exist in both populations independently. */}
      <Route path="/referral/*" element={<Referral />} />
      {/* A referral partner's readable link. Resolves the slug and forwards to
          self-serve signup carrying the code, so attribution is identical to
          the opaque form. Declared BEFORE nothing in particular, but kept next
          to /referral because they are the same feature. */}
      <Route path="/ref/:slug" element={<RefLanding />} />
      {/* PUBLIC preset showcase: a fixed scenario hosted for a named prospect
          (e.g. /try/practica). Same page in preset mode - no tailor/tune, full
          unlocked report. Unknown slugs bounce back to /try. */}
      <Route path="/try/:preset" element={<TryLanura />} />
      <Route
        path="/practice-simulation"
        element={<Navigate to="/practice-conversation" replace />}
      />
      <Route path="/skills-diagnostic" element={<Navigate to="/baseline-quiz" replace />} />
      {/* Onboarding: Welcome → Context → Baseline → Profile → Coaching setup → Focus */}
      <Route path="/baseline-quiz" element={<BaselineQuiz />} />
      <Route path="/baseline-quiz/context" element={<BaselineQuizContext />} />
      <Route path="/baseline-quiz/simulation" element={<BaselineQuizSimulation />} />
      <Route path="/baseline-quiz/profile" element={<BaselineQuizProfile />} />
      <Route path="/baseline-quiz/coaching" element={<BaselineQuizSnapshot />} />
      <Route path="/baseline-quiz/focus" element={<BaselineQuizFocus />} />
      {/* Alias for the step's own name */}
      <Route
        path="/baseline-quiz/baseline"
        element={<LegacyRedirect to="/baseline-quiz/simulation" />}
      />
      {/* Legacy redirects: the quiz / style / results screens are gone (the
          baseline conversation is the first evidence now), the snapshot became
          Coaching setup, and the two-page context flow is one page. Query
          strings (?sessionId=…) travel with the redirect. */}
      {['quiz', 'style', 'results', 'prompt', 'preview'].map((p) => (
        <Route
          key={p}
          path={`/baseline-quiz/${p}`}
          element={<LegacyRedirect to="/baseline-quiz/simulation" />}
        />
      ))}
      {['snapshot', 'preferences', 'confidence-goal'].map((p) => (
        <Route
          key={p}
          path={`/baseline-quiz/${p}`}
          element={<LegacyRedirect to="/baseline-quiz/coaching" />}
        />
      ))}
      {['role', 'situations'].map((p) => (
        <Route
          key={p}
          path={`/baseline-quiz/${p}`}
          element={<LegacyRedirect to="/baseline-quiz/context" />}
        />
      ))}
      <Route path="/baseline-quiz/finish" element={<Navigate to="/dashboard" replace />} />
      {/* Protected standalone routes without sidebar layout */}
      <Route
        path="/connect-calendar"
        element={
          <ProtectedRoute>
            <RouteErrorBoundary>
              <Suspense fallback={<PageSkeleton />}>
                <ConnectCalendar />
              </Suspense>
            </RouteErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <TrialBanner />
              <RouteErrorBoundary>
                <Suspense fallback={<PageSkeleton />}>
                  <Routes>
                    <Route
                      path="/dashboard"
                      element={
                        <PageTransition>
                          <HomePage />
                        </PageTransition>
                      }
                    />
                    <Route
                      path="/baseline/snapshot"
                      element={
                        <PageTransition>
                          <BaselineSnapshot />
                        </PageTransition>
                      }
                    />
                    <Route
                      path="/practice"
                      element={
                        <PageTransition>
                          <Practice />
                        </PageTransition>
                      }
                    />
                    <Route
                      path="/progress"
                      element={
                        <OnboardingRoute
                          locked={{
                            eyebrow: 'Progress',
                            title: 'Your progress unlocks after the diagnostic',
                            description:
                              'Progress tracks your skill trends, scores and weekly breakdowns. There’s nothing to show until you’ve done your diagnostic and a session or two.',
                            unlocks: [
                              'Skill trends from your scored sessions',
                              'Your weekly practice breakdown',
                            ],
                          }}
                        >
                          <PageTransition>
                            <Progress />
                          </PageTransition>
                        </OnboardingRoute>
                      }
                    />
                    {/* The old single page. Kept as a redirect so bookmarks,
                      coaching pills and the Overview footer all land on the
                      page that now does that job, carrying their query with
                      them. */}
                    <Route path="/admin/analytics" element={<AnalyticsRedirect />} />
                    <Route
                      path="/team/members"
                      element={
                        <PermissionRoute permission="manageTeam">
                          <PageTransition>
                            <TeamMembers />
                          </PageTransition>
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="/team/analytics"
                      element={
                        <PermissionRoute permission="manageTeam">
                          <PageTransition>
                            <TeamAnalytics />
                          </PageTransition>
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="/team/plans"
                      element={
                        <PermissionRoute permission="assignSimulations">
                          <PageTransition>
                            <PracticePlans />
                          </PageTransition>
                        </PermissionRoute>
                      }
                    />
                    {/* MPR6: assignments are their own Practice tab. */}
                    <Route
                      path="/team/assignments"
                      element={
                        <PermissionRoute permission="assignSimulations">
                          <PageTransition>
                            <PracticePlans view="assignments" />
                          </PageTransition>
                        </PermissionRoute>
                      }
                    />
                    {/* Manager Overview. One job: prioritise. Deliberately a
                      separate route rather than a fourth tab on
                      /admin/analytics, because the brief's rule is one job per
                      page and that page already does three. Additive, so the
                      analytics page is untouched. */}
                    {/* Overview is the manager's Home now (MH1). The address stays
                      so old links and bookmarks still land. */}
                    <Route path="/team/overview" element={<Navigate to="/dashboard" replace />} />
                    {/* No PermissionRoute: superAdmin is a pinned identity, not a
                      permission, so there is no flag to gate on. The page
                      redirects to /dashboard unless it is the pinned identity
                      ON the dev subdomain — approval-gated access exists
                      nowhere else, so the page would offer a control that
                      cannot do anything. Every endpoint behind it is
                      re-authorized server-side regardless. */}
                    {/* Owner/admin only. The page itself redirects anyone
                      else, and every endpoint it calls re-checks server-side. */}
                    <Route
                      path="/admin/rubrics"
                      element={
                        <PageTransition>
                          <RubricEditor />
                        </PageTransition>
                      }
                    />
                    {/* The queue is now an Internal Console section. Old links
                      and bookmarks land on it. */}
                    <Route
                      path="/admin/access-requests"
                      element={<Navigate to="/console?tab=access" replace />}
                    />
                    {/* Lanura's own tools, out of the client's Settings (N5).
                      The page redirects anyone but the pinned superAdmin, and
                      every endpoint behind it re-checks server-side. */}
                    <Route
                      path="/console"
                      element={
                        <PageTransition>
                          <InternalConsole />
                        </PageTransition>
                      }
                    />
                    <Route
                      path="/pathways"
                      element={
                        <OnboardingRoute
                          locked={{
                            eyebrow: 'Career paths',
                            title: 'Your career paths unlock after the diagnostic',
                            description:
                              'Career paths map the real-work situations to practise for your role and level - Lanura builds them from your onboarding diagnostic. Finish that and your personalised map appears here.',
                            unlocks: [
                              'A scenario map tailored to your role',
                              'Recommended role directions and fit',
                            ],
                          }}
                        >
                          <PageTransition>
                            <Pathways />
                          </PageTransition>
                        </OnboardingRoute>
                      }
                    />
                    <Route
                      path="/pathways/situations/:id"
                      element={
                        <OnboardingRoute
                          locked={{
                            eyebrow: 'Career paths',
                            title: 'Your career paths unlock after the diagnostic',
                            description:
                              'Career paths map the real-work situations to practise for your role and level - Lanura builds them from your onboarding diagnostic. Finish that and your personalised map appears here.',
                            unlocks: [
                              'A scenario map tailored to your role',
                              'Recommended role directions and fit',
                            ],
                          }}
                        >
                          <PageTransition>
                            <PathwaySituation />
                          </PageTransition>
                        </OnboardingRoute>
                      }
                    />
                    <Route path="/pathways/roles/:id" element={<PathwaysRoleRedirect />} />

                    {/* Legacy routes → tabbed hubs (preserve querystring for OAuth callbacks, etc.) */}
                    <Route path="/simulations" element={<Navigate to="/practice" replace />} />
                    <Route
                      path="/recordings"
                      element={<Navigate to="/practice?tab=recordings" replace />}
                    />
                    <Route path="/analytics" element={<Navigate to="/progress" replace />} />
                    <Route
                      path="/leaderboard"
                      element={<Navigate to="/progress?tab=leaderboard" replace />}
                    />
                    <Route
                      path="/resources"
                      element={<Navigate to="/settings?tab=workspace&section=context" replace />}
                    />
                    <Route
                      path="/integrations"
                      element={<Navigate to={withSearch('/settings?tab=integrations')} replace />}
                    />

                    <Route
                      path="/settings"
                      element={
                        <PageTransition>
                          <Settings />
                        </PageTransition>
                      }
                    />
                    <Route
                      path="/settings/preferences"
                      element={
                        <PageTransition>
                          <SettingsPreferences />
                        </PageTransition>
                      }
                    />
                    <Route
                      path="/simulation/:id"
                      element={
                        <PageTransition>
                          <SimulationSession />
                        </PageTransition>
                      }
                    />
                    <Route
                      path="/simulation-review"
                      element={
                        <PageTransition>
                          <SimulationReview />
                        </PageTransition>
                      }
                    />
                    <Route
                      path="/create-scenario"
                      element={
                        <PageTransition>
                          <CreateScenario />
                        </PageTransition>
                      }
                    />
                    <Route
                      path="/practice-conversation"
                      element={
                        <PageTransition>
                          <PracticeConversationEnhanced />
                        </PageTransition>
                      }
                    />
                    <Route
                      path="/practice-review"
                      element={
                        <PageTransition>
                          <PracticeReview />
                        </PageTransition>
                      }
                    />
                    <Route
                      path="/practice-presentation"
                      element={
                        <PageTransition>
                          <PracticePresentation />
                        </PageTransition>
                      }
                    />
                    <Route
                      path="/presentation-review"
                      element={
                        <PageTransition>
                          <PresentationReview />
                        </PageTransition>
                      }
                    />
                    <Route
                      path="/reflection"
                      element={
                        <PageTransition>
                          <Reflection />
                        </PageTransition>
                      }
                    />
                    {/* Subscription is owner-only (billing is owner-gated on the backend).
                      A non-owner reaching /subscription by editing the URL is sent to /dashboard. */}
                    <Route
                      path="/subscription"
                      element={
                        user && user.role !== 'owner' ? (
                          <Navigate to="/dashboard" replace />
                        ) : (
                          <PageTransition>
                            <Subscription />
                          </PageTransition>
                        )
                      }
                    />
                    <Route
                      path="/subscription/checkout-return"
                      element={
                        <PageTransition>
                          <CheckoutReturn />
                        </PageTransition>
                      }
                    />
                    <Route
                      path="/subscription/success"
                      element={
                        <PageTransition>
                          <CheckoutSuccess />
                        </PageTransition>
                      }
                    />
                    <Route
                      path="/subscription/cancelled"
                      element={
                        <PageTransition>
                          <CheckoutCancelled />
                        </PageTransition>
                      }
                    />
                    <Route path="*" element={<Navigate to="/dashboard" />} />
                  </Routes>
                </Suspense>
              </RouteErrorBoundary>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

const App: React.FC = () => {
  // PP-05: body.is-hidden pauses decorative loops (moon drift, aurora orbs,
  // skeleton sweeps) while the tab is in the background.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const sync = () => document.body.classList.toggle('is-hidden', document.hidden);
    sync();
    document.addEventListener('visibilitychange', sync);
    // PP-09: body.is-scrolling while the page (or any inner scroller) is
    // moving, cleared SCROLL_SETTLE_MS after the last scroll event. A running
    // CSS animation makes Blink recompute style on the main thread every
    // frame even when the effect is compositor-only, so the ambient loops
    // (moon drift, dashboard halo) hold still during a scroll and resume from
    // where they stopped. One listener, one class flip on <body>: the
    // invalidation set for .is-scrolling only names the looping elements, so
    // the flip itself costs a recalc of those two nodes, not the page.
    const SCROLL_SETTLE_MS = 400;
    let lastScroll = 0;
    let timer: number | null = null;
    const resume = () => {
      const remaining = SCROLL_SETTLE_MS - (performance.now() - lastScroll);
      if (remaining > 0) {
        timer = window.setTimeout(resume, remaining);
        return;
      }
      timer = null;
      document.body.classList.remove('is-scrolling');
    };
    const onScroll = () => {
      lastScroll = performance.now();
      if (timer !== null) return;
      document.body.classList.add('is-scrolling');
      timer = window.setTimeout(resume, SCROLL_SETTLE_MS);
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => {
      document.removeEventListener('visibilitychange', sync);
      document.removeEventListener('scroll', onScroll, { capture: true });
      if (timer !== null) window.clearTimeout(timer);
      document.body.classList.remove('is-scrolling');
    };
  }, []);
  const themeProviderContent = (
    <AuthProvider>
      <BackgroundEffectsProvider>
        <FrostedGlassProvider>
          <DarkModeProvider>
            <ConfirmProvider>
              {/* All framer-motion animations app-wide degrade for users with
                  prefers-reduced-motion (transform/layout animations skip,
                  opacity fades remain). */}
              <MotionConfig reducedMotion="user">
                <Router>
                  <div className="App">
                    <AppRoutes />
                    <ToastContainerWrapper />
                    <CookieConsent />
                  </div>
                </Router>
              </MotionConfig>
            </ConfirmProvider>
          </DarkModeProvider>
        </FrostedGlassProvider>
      </BackgroundEffectsProvider>
    </AuthProvider>
  );

  return (
    <QueryClientProvider client={queryClient}>
      {/* @ts-ignore - ThemeProvider has React 19 compatibility issue with ReactNode return type */}
      <ThemeProvider
        mode="light"
        colorsConfig={lanuraColorsConfig}
        typographyConfig={lanuraTypographyConfig}
        borderConfig={lanuraBorderConfig}
        neumorphismConfig={lanuraNeumorphismConfig}
      >
        {themeProviderContent}
      </ThemeProvider>
      {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};

export default App;
